// Copyright (c) StrangeBee 2023

import { ReactElement, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { RcFile } from 'antd/lib/upload';
import { DashboardInput, DashboardOutput } from 'thehive-sdk';
import DashboardForm from '@Organisms/Dashboard/DashboardDrawer/DashboardForm';
import useValidateForm from '@Hooks/useValidateForm';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { Alert, Space } from 'antd';
import { DashboardFormValues } from '@Organisms/Dashboard/DashboardDrawer/DashboardForm';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';
import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import { computeListSelector } from '../selector';
import './DashboardImport.scss';

interface DashboardImportProps {
    onImport: (dashboard: DashboardInput) => void;
    triggerElem?: ReactElement;
}

const DashboardImport = ({ onImport, triggerElem }: DashboardImportProps): ReactElement => {
    const i = useLocalMessage();
    const [importedFile, setImportedFile] = useState<RcFile | undefined>(undefined);
    const [hasError, setHasError] = useState<boolean>(false);
    const [dashboard, setDashboard] = useState<DashboardInput | undefined>();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!onImport,
    });

    const resetFile = () => {
        setImportedFile(undefined);
        setDashboard(undefined);
        setFieldsTouched(false);
        setHasError(false);
    };

    const importDashboard = (values: DashboardFormValues) => {
        if (dashboard) {
            onImport({ ...values, definition: dashboard.definition, version: dashboard.version });
        }
        resetForm();
    };

    const resetForm = () => {
        form.resetFields();
        setImportedFile(undefined);
        setDashboard(undefined);
        setHasError(false);
        setFieldsTouched(false);
    };

    const isDashboardCorrect = (dashboardData: any) => {
        const { definition, description, status, title } = dashboardData;
        return title && status && definition && description;
    };

    const createDashboardFromFile = (file: RcFile) => {
        if (!file) {
            setImportedFile(undefined);
            setDashboard(undefined);
            setHasError(false);
            return;
        }
        const reader = new FileReader();
        setImportedFile(file);

        reader.onload = ({ target }): void => {
            const data = target?.result;
            if (data) {
                const dashboardData = JSON.parse(data as string);

                if (isDashboardCorrect(dashboardData)) {
                    const { definition, description, group, status, title, version } = dashboardData;
                    setDashboard({ definition, description, group, status, title, version });
                    setHasError(false);
                } else {
                    setDashboard(undefined);
                    setHasError(true);
                    setFieldsTouched(false);
                }
                form.resetFields(); // reset form to set default values
            }
        };
        reader.readAsText(file);
    };

    const beforeUpload = (file: RcFile): boolean => {
        createDashboardFromFile(file);
        return false;
    };

    const { data: allDashboardList = [] } = useListQuery<DashboardOutput[]>({
        controller: client.DashboardController,
        queryParams: {
            selector: computeListSelector(),
        },
        manual: true,
        fetchOnStart: true,
    });

    return (
        <THDrawer
            title={i('dashboard.import.title')}
            onClose={resetFile}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) =>
                !dashboard ? (
                    <div className="dashboard-import__upload">
                        <Space direction="vertical" className="file-import__container" size="large">
                            <AlertImport title={i('dashboard.import.header.title')}>
                                <>{i('dashboard.import.header.content')}</>
                            </AlertImport>
                            <Space direction="vertical" className="file-import__upload">
                                <span className="file-import__upload-title">{i('general.import.upload.label')}</span>
                                <UploadSingleFile
                                    text={i('general.import.upload.content')}
                                    value={importedFile}
                                    onChange={(data) => beforeUpload(data as RcFile)}
                                    accept=".json"
                                />
                                {hasError && (
                                    <span className="dashboard-import__error">{i('dashboard.import.fileError')}</span>
                                )}
                                <span>{i('dashboard.import.instruction')}</span>
                            </Space>
                        </Space>
                    </div>
                ) : (
                    <DashboardForm
                        onCancel={() => {
                            resetForm();
                            toggleDrawer();
                        }}
                        isValid={dashboard && isValid}
                        form={form}
                        initialValues={dashboard}
                        onFinish={(values) => {
                            importDashboard(values);
                            toggleDrawer();
                            setFieldsTouched(false);
                        }}
                        onChange={() => {
                            onFormValuesChange();
                            setFieldsTouched(true);
                        }}
                        allDashboardList={allDashboardList}
                        isEditing={true}
                    >
                        {dashboard?.version !== 1 && (
                            <Alert
                                type="error"
                                message={
                                    <div className="dashboard-import__old-version">{i('dashboard.import.warning')}</div>
                                }
                            />
                        )}
                    </DashboardForm>
                )
            }
        </THDrawer>
    );
};

export default DashboardImport;
